/**
 * Forms Admin auth for /formsadmin
 * Credentials are stored hashed in localStorage (browser-side gate).
 * Default: admin / 2026 — must change on first login.
 */

const CREDS_KEY = "envision.formsAdmin.credentials.v1";
const SESSION_KEY = "envision.formsAdmin.session.v1";
const ATTEMPTS_KEY = "envision.formsAdmin.attempts.v1";

export const DEFAULT_USERNAME = "admin";
export const DEFAULT_PASSWORD = "2026";
const SESSION_MS = 12 * 60 * 60 * 1000;
const MAX_ATTEMPTS = 6;
const LOCKOUT_MS = 60 * 1000;

function randomSalt(bytes = 16) {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  return [...arr].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export async function hashPassword(password, salt) {
  const data = new TextEncoder().encode(`envision-forms-admin|${salt}|${password}`);
  const buf = await crypto.subtle.digest("SHA-256", data);
  return [...new Uint8Array(buf)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

/** Ensure default admin/2026 exists until the operator changes it. */
export async function ensureDefaultCredentials() {
  const existing = readJson(CREDS_KEY, null);
  if (existing?.username && existing?.passwordHash && existing?.salt) {
    return existing;
  }
  const salt = randomSalt();
  const passwordHash = await hashPassword(DEFAULT_PASSWORD, salt);
  const creds = {
    username: DEFAULT_USERNAME,
    salt,
    passwordHash,
    mustChangePassword: true,
    isDefaultPassword: true,
    updatedAt: new Date().toISOString(),
    createdAt: new Date().toISOString(),
  };
  writeJson(CREDS_KEY, creds);
  return creds;
}

export function getCredentialsMeta() {
  const c = readJson(CREDS_KEY, null);
  if (!c) {
    return {
      username: DEFAULT_USERNAME,
      mustChangePassword: true,
      isDefaultPassword: true,
      updatedAt: null,
    };
  }
  return {
    username: c.username || DEFAULT_USERNAME,
    mustChangePassword: !!c.mustChangePassword || !!c.isDefaultPassword,
    isDefaultPassword: !!c.isDefaultPassword,
    updatedAt: c.updatedAt || null,
  };
}

function getAttempts() {
  return readJson(ATTEMPTS_KEY, { fails: 0, lockedUntil: 0 });
}

function setAttempts(next) {
  writeJson(ATTEMPTS_KEY, next);
}

export function getLoginLockoutMs() {
  const a = getAttempts();
  const left = Number(a.lockedUntil || 0) - Date.now();
  return left > 0 ? left : 0;
}

export async function login(username, password) {
  await ensureDefaultCredentials();
  const lockMs = getLoginLockoutMs();
  if (lockMs > 0) {
    return {
      ok: false,
      error: `Too many attempts. Try again in ${Math.ceil(lockMs / 1000)}s.`,
    };
  }

  const creds = readJson(CREDS_KEY, null);
  const userOk =
    String(username || "").trim().toLowerCase() ===
    String(creds.username || DEFAULT_USERNAME).toLowerCase();
  const hash = await hashPassword(String(password || ""), creds.salt);
  const passOk = hash === creds.passwordHash;

  if (!userOk || !passOk) {
    const a = getAttempts();
    const fails = Number(a.fails || 0) + 1;
    const lockedUntil =
      fails >= MAX_ATTEMPTS ? Date.now() + LOCKOUT_MS : Number(a.lockedUntil || 0);
    setAttempts({
      fails: fails >= MAX_ATTEMPTS ? 0 : fails,
      lockedUntil,
    });
    return { ok: false, error: "Invalid username or password." };
  }

  setAttempts({ fails: 0, lockedUntil: 0 });
  const token = randomSalt(24);
  const session = {
    token,
    username: creds.username,
    createdAt: Date.now(),
    expiresAt: Date.now() + SESSION_MS,
  };
  writeJson(SESSION_KEY, session);
  return {
    ok: true,
    mustChangePassword: !!creds.mustChangePassword || !!creds.isDefaultPassword,
    session,
  };
}

export function logout() {
  localStorage.removeItem(SESSION_KEY);
}

export function getSession() {
  const session = readJson(SESSION_KEY, null);
  if (!session?.token || !session?.expiresAt) return null;
  if (Date.now() > Number(session.expiresAt)) {
    logout();
    return null;
  }
  return session;
}

export function isAuthenticated() {
  return !!getSession();
}

export async function changePassword(currentPassword, newPassword, confirmPassword) {
  if (!isAuthenticated()) {
    return { ok: false, error: "Please sign in again." };
  }
  await ensureDefaultCredentials();
  const creds = readJson(CREDS_KEY, null);
  const currentHash = await hashPassword(String(currentPassword || ""), creds.salt);
  if (currentHash !== creds.passwordHash) {
    return { ok: false, error: "Current password is incorrect." };
  }
  const next = String(newPassword || "");
  const confirm = String(confirmPassword || "");
  if (next.length < 4) {
    return { ok: false, error: "New password must be at least 4 characters." };
  }
  if (next !== confirm) {
    return { ok: false, error: "New password and confirmation do not match." };
  }
  if (next === DEFAULT_PASSWORD) {
    return {
      ok: false,
      error: `Choose a new password — do not keep the default (${DEFAULT_PASSWORD}).`,
    };
  }
  if (next === String(currentPassword || "")) {
    return { ok: false, error: "New password must be different from the current password." };
  }

  const salt = randomSalt();
  const passwordHash = await hashPassword(next, salt);
  writeJson(CREDS_KEY, {
    ...creds,
    salt,
    passwordHash,
    mustChangePassword: false,
    isDefaultPassword: false,
    updatedAt: new Date().toISOString(),
  });
  // Refresh session expiry after password change
  const session = getSession();
  if (session) {
    writeJson(SESSION_KEY, {
      ...session,
      expiresAt: Date.now() + SESSION_MS,
    });
  }
  return { ok: true };
}

export function mustChangePassword() {
  const meta = getCredentialsMeta();
  return !!meta.mustChangePassword;
}
